import _, {difference, mapValues} from 'lodash';

import {globalDocument} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {CompanyModel} from '../../libs/shared/core/src/models/api/company/companyModel';
import {TeammateModel} from '../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {CustomFieldModel} from '../models/api/customFieldModel';
import {
  HubspotIntegrationModel,
  IntegrationModel,
  IntegrationSettingsTypesEnum,
  MicrosoftDynamicsIntegrationModel,
  OutlookDirectoryIntegrationModel,
  SalesforceIntegrationModel,
} from '../models/api/integrationModel';
import {PreferencesModel} from '../models/api/preferencesModel';

/*
 * General helpers.
 */

export function isSidePanelIntegration(integration: IntegrationModel) {
  return (
    integration.hasPlugin &&
    !integration.hasMessageExtensions &&
    !integration.hasAttachmentExtensions &&
    !integration.hasSignatureExtensions
  );
}

export function isIntegrationSettingFieldLoading(field: string) {
  return field.includes('***');
}

/*
 * Integration data.
 */

export interface IntegrationDataSource {
  company: CompanyModel;
  user: TeammateModel;
  preferences: PreferencesModel;
  teammates: ReadonlyArray<TeammateModel>;
  integrations: ReadonlyArray<IntegrationModel>;
  customFields: ReadonlyArray<CustomFieldModel>;
}

export function buildIntegrationDataDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  return {
    ...buildIntegrationCompanyDiff(previous, current),
    ...buildIntegrationUserDiff(previous, current),
    ...buildIntegrationTeammatesDiff(previous, current),
    ...buildIntegrationIntegrationsDiff(previous, current),
    ...buildIntegrationCustomFieldsDiff(previous, current),
  };
}

function buildIntegrationCompanyDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  if (previous && current.company === previous.company) {
    return {};
  }

  return {company: companyToIntegrationJSON(current.company)};
}

function buildIntegrationUserDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  if (previous && current.user === previous.user && current.preferences === previous.preferences) {
    return {};
  }

  return {user: teammateToIntegrationJSON(current.user, current.preferences)};
}

function buildIntegrationTeammatesDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  const previousTeammates = previous && previous.teammates;
  if (current.teammates === previousTeammates) {
    return {};
  }

  const removedTeammates = difference(previousTeammates, current.teammates);
  const addedTeammates = difference(current.teammates, previousTeammates || []);

  if (removedTeammates.length === 0 && addedTeammates.length === 0) {
    return {};
  }

  const removedTeammatesNullMap = _(removedTeammates)
    .keyBy((t) => t.id)
    .mapValues(() => null)
    .value();
  const addedTeammatesMap = _(addedTeammates)
    .keyBy((t) => t.id)
    .mapValues((t) => teammateToIntegrationJSON(t))
    .value();

  return {
    teammates: {
      ...removedTeammatesNullMap,
      ...addedTeammatesMap,
    },
  };
}

function buildIntegrationIntegrationsDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  const previousIntegrations = previous && previous.integrations;
  if (current.integrations === previousIntegrations) {
    return {};
  }

  const removedIntegrations = difference(previousIntegrations, current.integrations);
  const addedIntegrations = difference(current.integrations, previousIntegrations || []);

  if (removedIntegrations.length === 0 && addedIntegrations.length === 0) {
    return {};
  }

  const removedIntegrationsNullMap = _(removedIntegrations)
    .keyBy((i) => i.id)
    .mapValues(() => null)
    .value();
  const addedIntegrationsMap = _(addedIntegrations)
    .keyBy((i) => i.id)
    .mapValues((i) => integrationToIntegrationJSON(i))
    .value();

  return {
    integrations: {
      ...removedIntegrationsNullMap,
      ...addedIntegrationsMap,
    },
  };
}

function buildIntegrationCustomFieldsDiff(
  previous: IntegrationDataSource | undefined,
  current: IntegrationDataSource,
) {
  const previousCustomFields = previous && previous.customFields;
  if (current.customFields === previousCustomFields) {
    return {};
  }

  const removedCustomFields = difference(previousCustomFields, current.customFields);
  const addedCustomFields = difference(current.customFields, previousCustomFields || []);

  if (removedCustomFields.length === 0 && addedCustomFields.length === 0) {
    return {};
  }

  const removedCustomFieldsNullMap = _(removedCustomFields)
    .keyBy((c) => c.id)
    .mapValues(() => null)
    .value();
  const addedCustomFieldsMap = _(addedCustomFields)
    .keyBy((c) => c.id)
    .mapValues((c) => customFieldToIntegrationJSON(c))
    .value();

  return {
    custom_fields: {
      ...removedCustomFieldsNullMap,
      ...addedCustomFieldsMap,
    },
  };
}

/*
 * Model transform.
 */

function companyToIntegrationJSON(company: CompanyModel) {
  return {
    id: company.id,
    url: company.url,
    name: company.name,
    subdomain: company.subdomain,
    preferences: company.preferences,
    updated_at: company.updatedAt,
  };
}

function teammateToIntegrationJSON(teammate: TeammateModel, preferences?: PreferencesModel) {
  const displayName = buildTeammateDisplayName(teammate);
  return {
    id: teammate.id,
    url: teammate.url,
    namespace: teammate.namespace,
    email: teammate.email,
    alias: teammate.alias,
    given_name: teammate.givenName,
    family_name: teammate.familyName,
    display_name: displayName,
    short_name: displayName,
    is_available: teammate.isAvailable,
    color: teammate.color,
    account_status: teammate.accountStatus,
    license_type: teammate.licenseType,
    access: mapValues(teammate.roleByNamespace, (role) => ({
      role,
      // For backward compatibility. Deprecated.
      roles: role === 'admin' ? ['member', 'admin'] : ['member'],
      // For backward compatibility. Was never guaranteed to be present/correct.
      inboxIds: [],
    })),
    avatar: teammate.avatarUrl,
    inbox_tag_id: teammate.inboxTagId,
    updated_at: teammate.updatedAt,
    preferences: preferences && preferencesToIntegrationJSON(preferences),
  };
}

function preferencesToIntegrationJSON(preferences: PreferencesModel) {
  return {
    emojiSkinVariation: preferences.emojiSkinTone,
    defaultNumberPrefix: preferences.defaultNumberPrefix,
    timeFormat: preferences.timeFormat,
  };
}

function integrationToIntegrationJSON(integration: IntegrationModel) {
  return {
    id: integration.id,
    type: {
      id: integration.type,
      name: integration.name,
      no_auth_url: integration.typeNoAuthUrl,
      url: integration.typeUrl,
      plugin: integration.hasPlugin,
    },
    name: integration.name,
    enabled: integration.isEnabled,
    settings: integration.settings,
    teammate_settings: integration.teammateSettings,
  };
}

function customFieldToIntegrationJSON(customField: CustomFieldModel) {
  return {
    id: customField.id,
    url: customField.url,
    type: customField.type,
    name: customField.name,
    name_slug: customField.nameSlug,
    origin: customField.origin,
    description: customField.description,
  };
}

/*
 * File integrations.
 */

export interface IntegrationFileAttachment {
  name: string;
  url: string;
  mimeType?: string;
}

export function buildIntegrationFileAttachmentHtml({name, url}: IntegrationFileAttachment) {
  const paragraph = globalDocument.createElement('div');

  const anchor = globalDocument.createElement('a');
  anchor.setAttribute('href', url);
  anchor.innerText = name;
  paragraph.appendChild(anchor);

  return paragraph.outerHTML;
}

export function buildIntegrationFileAttachmentMarkdown({name, url}: IntegrationFileAttachment) {
  return `![${name}](${url})`;
}

export function checkIsCrmIntegrationConnected(integration?: IntegrationModel) {
  switch (integration?.customSettingsType) {
    case IntegrationSettingsTypesEnum.SALESFORCE: {
      return checkIsSalesforceConnected(integration);
    }
    case IntegrationSettingsTypesEnum.HUBSPOT: {
      return checkIsHubspotConnected(integration);
    }
    case IntegrationSettingsTypesEnum.OUTLOOK_DIRECTORY:
    case IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS: {
      return checkIsMicrosoftIntegrationConnected(integration);
    }
    default: {
      return false;
    }
  }
}

export function extractCrmIntegrationUsername(integration?: IntegrationModel): string | undefined {
  switch (integration?.customSettingsType) {
    case IntegrationSettingsTypesEnum.SALESFORCE: {
      return integration.settings.readonly?.user?.name;
    }
    default: {
      // @ts-expect-error It is ok to return undefined here, but TS complains about readonly not existing on type object
      return integration?.settings?.readonly?.username;
    }
  }
}

export function extractMicrosoftDynamicsEnvironmentUrl(integration?: IntegrationModel): string | undefined {
  if (integration?.customSettingsType !== IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS) {
    return undefined;
  }
  return integration?.settings.readonly?.environmentBaseUrl;
}

/*
 * Salesforce Helpers.
 */

export function checkIsSalesforceConnected(integration?: SalesforceIntegrationModel) {
  return Boolean(integration && integration.isEnabled && integration.settings.readonly?.user);
}

/*
 * Hubspot Helpers.
 */

export function checkIsHubspotConnected(integration?: HubspotIntegrationModel) {
  return Boolean(integration && integration.isEnabled && integration.settings.readonly?.loggedIn);
}

/*
 * Microsoft Helpers.
 */

export function checkIsMicrosoftIntegrationConnected(
  integration?: MicrosoftDynamicsIntegrationModel | OutlookDirectoryIntegrationModel,
) {
  return Boolean(integration && integration.isEnabled && integration.settings.readonly?.username);
}
